import { z } from "zod";
import { checkPeriod } from "./conversations.js";
import { isoDaySchema } from "./filters.js";
import { LEAD_SOURCE_SYSTEMS } from "./leads.js";
import { personSchema } from "./people.js";
import { ID_PATTERN, idSchema } from "./text.js";
// Il Report «Commerciali» (piano M7, Task 2; D2–D6; SEZ-1, SEZ-2, SEZ-3, SEZ-16; mock-up del 17/10/2026): `GET
// /api/reports/sales`, i conteggi grezzi del periodo e di quello prima, per la somma delle persone scelte, persona per persona
// e giorno per giorno. Percentuali, minuti, valore medio, frecce e colori li calcola il dominio nel browser (`report-kpis.ts`),
// sempre sui conteggi sommati: il server non manda mai una percentuale.
//
// Gli elenchi ripetono quelli del dominio (`@gioia365/domain`: `report-period.ts`, `report-kpis.ts`, `report-grid.ts`, `work-blocks.ts`),
// che i contratti non importano: un test del server li confronta voce per voce (`reports-sales.test.ts`, `reports-work-blocks.test.ts`).
/** I periodi di SEZ-3, con le parole di «Conversazioni» tranne «Tutte»: «Oggi», «Ieri», «Settimana», «Mese», «Personalizzato». */
export const REPORT_PERIODS = ["today", "yesterday", "last7", "last30", "custom"];
/** Quante persone «Di chi» sceglie al più in una richiesta. */
export const REPORT_WHO_MAX = 50;
/** Il separatore delle persone di «Di chi» nella query: `who=<id>,<id>`. */
export const REPORT_WHO_SEPARATOR = ",";
/** Gli identificativi di «Di chi» scritti nella query, nell'ordine in cui arrivano. */
export function reportWhoIdsOf(who) {
    return who.split(REPORT_WHO_SEPARATOR);
}
// Al più 50 identificativi da 128 caratteri con le virgole fra loro: una query più lunga non si divide nemmeno.
const WHO_MAX_LENGTH = REPORT_WHO_MAX * 129;
/**
 * Query di `GET /api/reports/sales`. `period` di partenza `last30` («Mese», SEZ-2); `from` e `to` (`AAAA-MM-GG`) soltanto con
 * `custom`, tutti e due, e `from` non dopo `to` (la regola di «Conversazioni», `checkPeriod`); un «Personalizzato» che arriva dopo
 * oggi o è più lungo di 366 giorni lo ferma il server con `422 report_period_invalid` e il motivo (`future`, `too_long`). `who`
 * («Di chi») sono da 1 a 50 identificativi di appartenenze separati da una virgola: vale soltanto per chi vede i report del team
 * (`reports.view: all`); per chi vede i suoi non allarga e non restringe nulla. Un identificativo che non è una persona
 * dell'azienda risponde `404 not_found`, come se non ci fosse.
 */
export const reportsQuerySchema = z
    .strictObject({
    period: z.enum(REPORT_PERIODS).default("last30"),
    from: isoDaySchema.optional(),
    to: isoDaySchema.optional(),
    who: z
        .string()
        .max(WHO_MAX_LENGTH)
        .refine((who) => {
        const ids = reportWhoIdsOf(who);
        return ids.length <= REPORT_WHO_MAX && ids.every((id) => ID_PATTERN.test(id));
    }, { message: `who is 1 to ${REPORT_WHO_MAX} membership ids separated by commas` })
        .optional(),
})
    .superRefine(checkPeriod);
const countSchema = z.number().int().nonnegative();
/**
 * I conteggi grezzi di un periodo (`ReportMetrics` del dominio), per una persona, un giorno o la somma delle persone scelte:
 * chiamate in uscita valide, con risposta, Lead diversi e Lead con risposta (contati una volta sull'unione, quindi non si
 * sommano), chiuse presto (fuori dalle chiamate, TEL-10), inserite a mano (dentro le chiamate), secondi di chiamata; disco e demo
 * prenotate, da fare, effettuate, riprogrammate e minuti; chiusure e valore in centesimi.
 */
export const reportMetricsSchema = z.object({
    calls: countSchema,
    answered: countSchema,
    leads: countSchema,
    answeredLeads: countSchema,
    closedEarly: countSchema,
    manualCalls: countSchema,
    callSeconds: countSchema,
    discoBooked: countSchema,
    discoDue: countSchema,
    discoHeld: countSchema,
    discoRescheduled: countSchema,
    discoMinutes: countSchema,
    demoBooked: countSchema,
    demoDue: countSchema,
    demoHeld: countSchema,
    demoRescheduled: countSchema,
    demoMinutes: countSchema,
    // Fra le prenotate, quelle storiche entrate da Close (22/10/2026): già comprese, si dicono a parte.
    discoFromClose: countSchema.default(0),
    demoFromClose: countSchema.default(0),
    closings: countSchema,
    valueCents: countSchema,
});
/** Le chiavi dei conteggi, nell'ordine del dominio (`REPORT_METRIC_KEYS`). */
export const REPORT_METRIC_KEYS = Object.freeze(Object.keys(reportMetricsSchema.shape));
/** Un obiettivo in percento: almeno `min`, e fino a `max` se c'è («8–10%»; «almeno 50%» non ha massimo). */
export const reportTargetSchema = z.object({ min: z.number(), max: z.number().optional() });
/** Gli obiettivi dei colori (`ReportTargets` del dominio): i decisi di SEZ-2 e gli altri di «Calcolo Sales» o salvati. */
export const reportTargetsSchema = z.object({
    discoOnCalls: reportTargetSchema,
    discoOnAnswered: reportTargetSchema,
    discoShow: reportTargetSchema,
    demoOnDisco: reportTargetSchema,
    demoShow: reportTargetSchema,
    closeRate: reportTargetSchema,
});
/**
 * Un periodo come lo legge la pagina: i giorni `AAAA-MM-GG` compresi e, quando il periodo del report arriva a oggi, l'istante in
 * cui finisce (`endsAt`, ISO): adesso per il periodo, la stessa ora del giorno prima per il confronto («fino alle 16:20»).
 */
export const reportRangeViewSchema = z.object({ from: z.string(), to: z.string(), endsAt: z.string().nullable() });
/** Una persona di «Di chi» e delle righe: il nome di adesso e se è ancora attiva, come nelle altre risposte (`personSchema`). */
export const reportPersonSchema = personSchema;
/** Lo stato di una cella della griglia delle ore (`CALL_GRID_CELL_STATES` del dominio, `report-grid.ts`): lavoro, pausa, chiuso. */
export const CALL_GRID_CELL_STATES = ["work", "pause", "closed"];
const hourSchema = z.number().int().min(0).max(23);
/**
 * La griglia delle chiamate del periodo (piano M7, Task 4; «Quando si chiama» e l'andamento di un giorno solo): le ore delle colonne
 * sull'orologio dell'azienda, i minuti della prima (`30` per «8:30»), se il venerdì la sequenza chiama soltanto la mattina, e una
 * riga per giorno della settimana (ISO, lunedì 1 … domenica 7) con lavoro o con chiamate, ogni cella con il suo stato, le chiamate
 * valide e quelle con risposta delle persone scelte, sommate sui giorni del periodo.
 */
export const callGridSchema = z.object({
    hours: z.array(hourSchema),
    firstMinute: z.number().int().min(0).max(59),
    fridayMorningOnly: z.boolean(),
    rows: z.array(z.object({
        weekday: z.number().int().min(1).max(7),
        cells: z.array(z.object({ hour: hourSchema, state: z.enum(CALL_GRID_CELL_STATES), calls: countSchema, answered: countSchema })),
    })),
});
/**
 * «Obiettivi del mese» (piano M7, Task 6; D9; SEZ-16): il mese di calendario che contiene oggi, qualunque sia il periodo scelto, con la
 * somma degli obiettivi di vendita delle persone scelte (con «Tutto il team» quelli di tutti; con `own` il suo), il valore medio di una
 * vendita, quante persone hanno un obiettivo e quante sono, ciò che hanno fatto dal primo del mese ad adesso le sole persone con un
 * obiettivo (soltanto il totale, con le stesse regole dei numeri) e i giorni lavorativi del mese, quelli trascorsi (oggi per la parte già
 * lavorata) e quelli fino a oggi compreso (il «giorno lavorativo {n} di {m}»). La risalita dalle
 * vendite alle chiamate e il ritmo li calcola il dominio nel browser (`goalPlanOf`). Senza un obiettivo, o senza il valore medio, dice
 * soltanto che cosa manca.
 */
export const reportGoalsViewSchema = z.union([
    z.strictObject({
        month: z.string(),
        from: z.string(),
        to: z.string(),
        salesGoalCents: countSchema,
        averageSaleCents: z.number().int().min(1),
        peopleWithGoal: countSchema,
        peopleChosen: countSchema,
        done: reportMetricsSchema,
        workingDays: countSchema,
        elapsedWorkingDays: z.number().nonnegative(),
        workingDaysSoFar: countSchema,
    }),
    z.strictObject({ missing: z.enum(["goal", "average_sale"]), month: z.string(), from: z.string(), to: z.string() }),
]);
// La giornata a blocchi di 30 minuti (piano M7, Task 7; D10; SEZ-4, SEZ-16; domanda n. 34, aperta): spenta in ogni azienda finché il
// Super admin di Gioia365 non la accende (`PUT /api/settings/work-blocks`, soltanto dal contesto di piattaforma). `GET
// /api/reports/work-blocks` la legge con le regole di permesso degli altri report: spenta risponde soltanto `{ enabled: false }`, senza
// leggere né calcolare altro; accesa, le mezz'ore di una persona in un giorno, ognuna con il suo stato e i numeri della persona.
/** Lo stato di un blocco (`WORK_BLOCK_STATES` del dominio, `work-blocks.ts`): al telefono, solo al computer, fermo, pausa, chiuso, non ancora. */
export const WORK_BLOCK_STATES = ["phone", "screen", "idle", "pause", "closed", "future"];
/**
 * Query di `GET /api/reports/work-blocks`: la persona (di partenza quella della richiesta; nella «Guarda come…» quella osservata) e il
 * giorno `AAAA-MM-GG` (di partenza l'ultimo giorno lavorativo fino a oggi; la pagina manda quello scelto fra i giorni lavorativi del
 * periodo). Chi vede i suoi report legge soltanto la sua giornata: un collega risponde `404 not_found`, come una persona che non è
 * dell'azienda; un giorno dopo oggi `422 validation_failed` con `day`.
 */
export const workBlocksQuerySchema = z.strictObject({ person: idSchema.optional(), day: isoDaySchema.optional() });
/** Una mezz'ora: inizio e fine (ISO), lo stato, le chiamate in uscita della persona, quelle con risposta, i loro secondi e le altre attività. */
export const workBlockSchema = z.strictObject({
    start: z.string(),
    end: z.string(),
    state: z.enum(WORK_BLOCK_STATES),
    calls: countSchema,
    answered: countSchema,
    callSeconds: countSchema,
    activities: countSchema,
});
export const workBlocksResponseSchema = z.discriminatedUnion("enabled", [
    z.strictObject({ enabled: z.literal(false) }),
    z.strictObject({ enabled: z.literal(true), person: reportPersonSchema, day: z.string(), blocks: z.array(workBlockSchema) }),
]);
/** Corpo di `PUT /api/settings/work-blocks`: accesa o spenta, con la versione letta della riga delle impostazioni del Report. */
export const workBlocksSwitchBodySchema = z.strictObject({ enabled: z.boolean(), expectedVersion: z.number().int().min(1) });
/** L'interruttore come lo legge la pagina (in «Commerciali») e come lo ridà il salvataggio: acceso o no, e la versione delle impostazioni. */
export const workBlocksSettingSchema = z.strictObject({ enabled: z.boolean(), version: z.number().int().min(1) });
export const salesReportResponseSchema = z.object({
    range: reportRangeViewSchema,
    previous: reportRangeViewSchema,
    /** `own`: i numeri della persona (nella «Guarda come…» di quella osservata); `all`: il team, con «Di chi». */
    scope: z.enum(["own", "all"]),
    /**
     * Le scelte di «Di chi», in ordine alfabetico italiano: gli owner possibili attivi, chi ha numeri nel periodo e le persone
     * scelte; con `own` soltanto la persona.
     */
    people: z.array(reportPersonSchema),
    /** Le persone applicate, in ordine alfabetico; vuoto vuol dire «Tutto il team» (e sempre con `own`). */
    chosen: z.array(z.string()),
    /** Il periodo e quello prima, per la somma delle persone scelte: i Lead diversi una volta sull'unione. */
    totals: z.object({ current: reportMetricsSchema, previous: reportMetricsSchema }),
    /** «Una riga per Commerciale»: con «Tutto il team» ogni persona di `people`, anche a zero; con «Di chi» le scelte. Nullo con `own`. */
    byPerson: z.array(z.object({ person: reportPersonSchema, metrics: reportMetricsSchema })).nullable(),
    /** «Giorno per giorno»: i giorni lavorativi del periodo e quelli chiusi con lavoro, dal primo all'ultimo; `working` dice quali. */
    days: z.array(z.object({ day: z.string(), working: z.boolean(), metrics: reportMetricsSchema })),
    /** I giorni lavorativi del periodo prima (per la media al giorno del confronto). */
    previousWorkingDays: countSchema,
    /** «Quando si chiama»: le chiamate del periodo per giorno della settimana e ora; con un giorno solo, la sua riga. */
    callGrid: callGridSchema,
    /** Gli obiettivi dei colori: i decisi di SEZ-2 e gli altri salvati in Impostazioni → «Obiettivi», o di partenza di «Calcolo Sales». */
    targets: reportTargetsSchema,
    goals: reportGoalsViewSchema,
    /** La giornata a blocchi di 30 minuti: se è accesa nell'azienda e la versione delle impostazioni del Report (per l'interruttore). */
    workBlocks: workBlocksSettingSchema,
});
// «Campagne» (piano M7, Task 5; D8; INGR-5, SEZ-10, SEZ-15, SEZ-16; mock-up `renderCampagne`): `GET /api/reports/campaigns`, con le
// stesse regole di permesso, periodo e «Di chi» di «Commerciali». Una riga per sorgente e nome della campagna: «Lead nuovi» gli arrivi
// che hanno fatto nascere un Lead nel periodo, «Rientrati» quelli che hanno raggiunto un Lead che c'era già, ognuno sotto la campagna
// da cui è arrivato e, con «Di chi», all'owner che il Lead ha avuto all'ingresso; disco prenotate e vendite del periodo sotto la prima
// provenienza del Lead, che non cambia mai (un Lead di Close è «Close», uno senza provenienza ha la sua riga), la disco a chi l'ha
// prenotata e la vendita a chi l'ha chiusa. Le vendite elencate sono quelle chiuse dalle persone scelte e quelle con la disco prenotata
// da loro e chiusa da un collega, che contano a lui («Conta a …, che ha chiuso»). Le demo effettuate (per il close rate) vanno anche
// loro alla prima provenienza, a chi le ha prenotate, al giorno dell'appuntamento. Il server manda soltanto conteggi e centesimi:
// valore medio, close rate e frecce li calcola il dominio nel browser.
/** Le sorgenti di una campagna, le stesse della colonna «Provenienza» dell'elenco Lead (`LEAD_SOURCE_SYSTEMS`). */
export const CAMPAIGN_SOURCES = LEAD_SOURCE_SYSTEMS;
/**
 * I numeri di una campagna in un periodo: Lead nuovi, rientrati, disco prenotate, demo effettuate (per il close rate, vendite ÷ demo
 * effettuate, come in «Commerciali»), vendite e il loro valore in centesimi.
 */
export const campaignMetricsSchema = z.object({ fresh: countSchema, back: countSchema, disco: countSchema, demoHeld: countSchema, closings: countSchema, valueCents: countSchema });
const campaignOfSchema = z.object({ source: z.enum(CAMPAIGN_SOURCES), campaign: z.string().nullable() });
const doneBySchema = z.object({ person: reportPersonSchema, at: z.string() });
/**
 * Una vendita di «Vendite per campagne», con «Chi ha fatto cosa»: per disco e demo l'ultima effettuata dell'Opportunità, altrimenti
 * l'ultima non annullata, con chi l'ha prenotata e quando (l'ora dell'appuntamento); chi ha chiuso e i campi di «Venduto». `lead` è
 * nullo per un Lead che chi guarda non vede: la vendita resta, senza nome, salone né città. `latest` è l'ultimo ingresso del Lead,
 * soltanto se è un altro della prima provenienza; i tre campi del salone sono le voci della scheda.
 */
export const reportSaleSchema = z.object({
    opportunityId: z.string(),
    closedAt: z.string(),
    lead: z
        .object({
        id: z.string(),
        displayName: z.string().nullable(),
        salonName: z.string().nullable(),
        city: z.string().nullable(),
        addetti: z.string().nullable(),
        ficheMedia: z.string().nullable(),
        presenzeMensili: z.string().nullable(),
    })
        .nullable(),
    first: campaignOfSchema.nullable(),
    latest: campaignOfSchema.extend({ at: z.string() }).nullable(),
    disco: doneBySchema.nullable(),
    demo: doneBySchema.nullable(),
    closedBy: reportPersonSchema,
    /** Vero quando chi ha chiuso non è fra le persone scelte (con «Tutto il team» mai): la vendita conta a lui. */
    countsToCloser: z.boolean(),
    /** Club o Percorso; `null` per una vendita importata da Close, che non lo dice (INGR-26): l'elenco scrive «Non indicato». */
    product: z.enum(["club", "percorso"]).nullable(),
    contractType: z.string().nullable(),
    offer: z.string().nullable(),
    paymentStartDate: z.string().nullable(),
    bonus: z.string().nullable(),
    valueCents: countSchema,
});
export const campaignsReportResponseSchema = z.object({
    range: reportRangeViewSchema,
    previous: reportRangeViewSchema,
    scope: z.enum(["own", "all"]),
    /** Le scelte di «Di chi», come in «Commerciali». */
    people: z.array(reportPersonSchema),
    chosen: z.array(z.string()),
    /**
     * Le campagne con un numero nel periodo, in ordine di valore e poi di Lead nuovi, ognuna con i numeri del periodo prima; `source`
     * nullo è «Senza provenienza».
     */
    campaigns: z.array(z.object({ source: z.enum(CAMPAIGN_SOURCES).nullable(), campaign: z.string().nullable(), current: campaignMetricsSchema, previous: campaignMetricsSchema })),
    /** La somma di tutte le campagne, anche di quelle che hanno numeri soltanto nel periodo prima. */
    totals: z.object({ current: campaignMetricsSchema, previous: campaignMetricsSchema }),
    /** Le vendite del periodo, dalla più recente. */
    sales: z.array(reportSaleSchema),
    /** Gli obiettivi dei colori, gli stessi di «Commerciali»: qui serve quello del close rate. */
    targets: reportTargetsSchema,
});
//# sourceMappingURL=reports.js.map